/**
 * Sentence boundaries (de/en): period/exclamation/question + space + capital or quote/digit.
 */
const SENTENCE_BOUNDARY = /(?<=[.!?])\s+(?=[A-ZÄÖÜ«"„"'(„\d])/u;

/**
 * Unescapes literal `\n`, `\t`, `\r` sequences (two-character escapes) that sometimes appear
 * when JSON or API output was stored as plain text instead of real newlines.
 */
function unescapeLiteralEscapes(s: string): string {
  if (!s.includes('\\')) return s;
  return s
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\\t/g, '\t');
}

/**
 * Reads a JSON string value starting at `startAt` (first char inside the opening `"`).
 * Handles invalid/truncated JSON where newlines appear unescaped inside the string (common
 * with LLM output): a closing `"` is only recognized when followed by `,` or `}`.
 */
function scanJsonLikeQuotedStringValue(text: string, startAt: number): string {
  let i = startAt;
  let out = '';
  while (i < text.length) {
    const c = text[i];
    if (c === '\\') {
      if (i + 1 >= text.length) {
        out += '\\';
        break;
      }
      const n = text[i + 1];
      if (n === 'n') {
        out += '\n';
        i += 2;
        continue;
      }
      if (n === 'r') {
        out += '\r';
        i += 2;
        continue;
      }
      if (n === 't') {
        out += '\t';
        i += 2;
        continue;
      }
      if (n === '"' || n === '\\') {
        out += n;
        i += 2;
        continue;
      }
      if (n === 'u' && i + 6 <= text.length) {
        const hex = text.slice(i + 2, i + 6);
        if (/^[0-9a-fA-F]{4}$/.test(hex)) {
          out += String.fromCharCode(parseInt(hex, 16));
          i += 6;
          continue;
        }
      }
      out += n;
      i += 2;
      continue;
    }
    if (c === '"') {
      let j = i + 1;
      while (j < text.length && /\s/.test(text.charAt(j))) j++;
      if (j < text.length && (text.charAt(j) === '}' || text.charAt(j) === ',')) {
        return out;
      }
      out += '"';
      i++;
      continue;
    }
    out += c;
    i++;
  }
  return out;
}

function tryExtractLenientJsonStringField(t: string, key: 'summary' | 'text' | 'content'): string | null {
  const re = new RegExp(`^\\s*\\{\\s*"${key}"\\s*:\\s*"`);
  const m = t.match(re);
  if (!m) return null;
  const extracted = scanJsonLikeQuotedStringValue(t, m[0].length);
  const trimmed = extracted.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * LLM output often glues the next list item onto the previous line: "… Mobile. 2. Swisscom …"
 * Split before `N.` when it clearly starts a new numbered point (capital letter / quote after).
 * Skips common German abbreviations ending with a period (vgl., z. B., Nr., ca.).
 */
const DE_ABBREV_BEFORE_PERIOD = new Set(
  [
    'vgl',
    'bzw',
    'etc',
    'ca',
    'chr',
    'abs',
    'nr',
    'ggf',
    'sog',
    'evtl',
    'mind',
    'max',
    'inkl',
    'u.a',
    'u.ä',
    'z.b',
    'd.h',
    's.v',
    'tab',
    'abb',
    'kap',
    'art',
    'ff',
    'usw',
    'siehe',
    'anm',
    'insb',
    'std',
    'ev',
  ].map((w) => w.toLowerCase())
);

function lastWordBeforeIndex(text: string, periodIndex: number): string {
  const before = text.slice(0, periodIndex).trimEnd();
  const m = before.match(/([\p{L}\p{N}][\p{L}\p{N}\p{M}.-]*)\s*$/u);
  if (!m) return '';
  return (m[1] ?? '').replace(/[.,;:]+$/u, '').toLowerCase();
}

function fixInlineNumberedListMarkers(s: string): string {
  const re = /([.!?])\s+(\d{1,2})\.\s+(?=[A-ZÄÖÜ„"«(])/gu;
  return s.replace(re, (full, punct: string, num: string, offset: number, str: string) => {
    const periodIdx = offset;
    const w = lastWordBeforeIndex(str, periodIdx);
    if (w && DE_ABBREV_BEFORE_PERIOD.has(w)) return full;
    if (w && /^\d{4}$/.test(w)) return full;
    return `${punct}\n${num}. `;
  });
}

/** Merges a line that is only `1.` (or `2.`) with the following paragraph so Markdown lists render correctly. */
function fixOrphanNumberedListLines(s: string): string {
  // Do not use `\s*` before `\n+`: `\s` matches newlines and would consume them so `\n+` never matches.
  return s.replace(/(^|\n)(\d{1,2})\.(?:[ \t]*\r?\n)+(?!\d{1,2}\.\s)/g, '$1$2. ');
}

/** Collapse double blank lines between consecutive `N. …` lines so lists render as one block. */
function collapseBlankLinesBetweenNumberedItems(s: string): string {
  let t = s;
  for (let i = 0; i < 8; i++) {
    const next = t.replace(/(^|\n)(\d+\.\s+[^\n]+)\n{2,}(?=\d+\.\s)/g, '$1$2\n');
    if (next === t) break;
    t = next;
  }
  return t;
}

/** Strip accidental leading spaces before line-start list markers (`  1. …` → `1. …`). */
function stripLeadingIndentBeforeListMarkers(s: string): string {
  return s.replace(/(^|\n)( +)(?=\d{1,2}\.\s)/g, '$1');
}

/** Strip leading spaces before `Phase N …` at line start (models indent pseudo-headings). */
function stripLeadingIndentBeforePhaseHeaders(s: string): string {
  return s.replace(/(^|\n)( +)(?=Phase\s+\d+)/giu, '$1');
}

/**
 * Roadmap text often chains `… Standorte. Phase 2 (6–12 Monate):` without a leading `2.`
 * — break before `Phase 2+` when it looks like a section header (optional Klammer, then ':').
 */
function splitBeforePhaseTwoOrLater(s: string): string {
  const re = /([.!?])\s+(?=Phase\s+(?:[2-9]|1[0-9])\s*(?:\([^)]*\))?\s*:)/giu;
  return s.replace(re, (full, punct: string, offset: number, str: string) => {
    const periodIdx = offset;
    const w = lastWordBeforeIndex(str, periodIdx);
    if (w && /^\d{4}$/.test(w)) return full;
    return `${punct}\n`;
  });
}

function splitBeforePhaseTwoOrLaterUntilStable(s: string, maxPasses = 12): string {
  let t = s;
  for (let i = 0; i < maxPasses; i++) {
    const next = splitBeforePhaseTwoOrLater(t);
    if (next === t) break;
    t = next;
  }
  return t;
}

/** Collapse extra blank lines between consecutive `Phase N …` lines. */
function collapseBlankLinesBetweenPhaseHeaders(s: string): string {
  let t = s;
  for (let i = 0; i < 8; i++) {
    const next = t.replace(/(^|\n)(Phase\s+\d+[^\n]+)\n{2,}(?=Phase\s+\d+)/giu, '$1$2\n');
    if (next === t) break;
    t = next;
  }
  return t;
}

/**
 * Repeatedly split inline `… Satz. 2. Nächster Punkt` until stable (models often chain many items).
 */
function fixInlineNumberedListMarkersUntilStable(s: string, maxPasses = 14): string {
  let t = s;
  for (let i = 0; i < maxPasses; i++) {
    const next = fixInlineNumberedListMarkers(t);
    if (next === t) break;
    t = next;
  }
  return t;
}

/** Ordered list + roadmap phase blocks — inline markers, spacing, orphan line numbers. */
function normalizeNumberedListsInPlainText(s: string): string {
  let t = splitBeforePhaseTwoOrLaterUntilStable(s);
  t = fixInlineNumberedListMarkersUntilStable(t);
  t = collapseBlankLinesBetweenNumberedItems(t);
  t = collapseBlankLinesBetweenPhaseHeaders(t);
  t = fixOrphanNumberedListLines(t);
  t = stripLeadingIndentBeforeListMarkers(t);
  t = stripLeadingIndentBeforePhaseHeaders(t);
  return t;
}

/**
 * Fixes raw API blobs: full JSON objects persisted as text, markdown fences, and literal
 * `\\n` sequences — so MarkdownText can split lines and format sections correctly.
 */
export function normalizeResearchSummaryRaw(raw: string): string {
  const t = raw.trim();
  if (!t) return '';

  const tryParseJsonSummary = (jsonStr: string): string | null => {
    const trimmed = jsonStr.trim();
    try {
      const parsed = JSON.parse(trimmed) as {
        summary?: unknown;
        text?: unknown;
        content?: unknown;
      };
      const inner =
        (typeof parsed.summary === 'string' ? parsed.summary : null) ??
        (typeof parsed.text === 'string' ? parsed.text : null) ??
        (typeof parsed.content === 'string' ? parsed.content : null);
      if (inner != null && String(inner).trim()) {
        return unescapeLiteralEscapes(String(inner).trim());
      }
    } catch {
      // ignore
    }
    return null;
  };

  const fenceInner = t.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fenceInner) {
    const fenceBody = (fenceInner[1] ?? '').trim();
    const parsedFence = tryParseJsonSummary(fenceBody);
    if (parsedFence) return normalizeNumberedListsInPlainText(parsedFence);
    if (fenceBody.startsWith('{')) {
      for (const key of ['summary', 'text', 'content'] as const) {
        const lenient = tryExtractLenientJsonStringField(fenceBody, key);
        if (lenient) return normalizeNumberedListsInPlainText(lenient);
      }
    }
  }

  if (t.startsWith('{')) {
    const parsed = tryParseJsonSummary(t);
    if (parsed) return normalizeNumberedListsInPlainText(parsed);
    const start = t.indexOf('{');
    const end = t.lastIndexOf('}');
    if (start >= 0 && end > start) {
      const inner = tryParseJsonSummary(t.slice(start, end + 1));
      if (inner) return normalizeNumberedListsInPlainText(inner);
    }
    for (const key of ['summary', 'text', 'content'] as const) {
      const lenient = tryExtractLenientJsonStringField(t, key);
      if (lenient) return normalizeNumberedListsInPlainText(lenient);
    }
  }

  return normalizeNumberedListsInPlainText(unescapeLiteralEscapes(t));
}

/**
 * Sentence split treats `1.` + `Phase …` as two fragments (period + space + capital). Merge
 * orphan numeric markers back onto the following sentence so MarkdownText can render `1. Phase …`
 * as one ordered-list line.
 */
function mergeOrphanListNumberWithFollowingSentence(sentences: string[]): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < sentences.length) {
    const raw = sentences[i]!;
    const s = raw.trim();
    if (!s) {
      i += 1;
      continue;
    }
    const trimmedEnd = raw.trimEnd();
    /** Sentence ends with a line that is only `N.` (common after SENTENCE_BOUNDARY split). */
    const endsWithBareNumberLine = /(?:^|\n)(\d{1,2})\.\s*$/.test(trimmedEnd);
    if (endsWithBareNumberLine && i + 1 < sentences.length) {
      const next = sentences[i + 1]!.trim();
      if (next && !/^\d{1,2}\.\s/.test(next)) {
        out.push(`${trimmedEnd} ${next}`);
        i += 2;
        continue;
      }
    }
    if (/^\d{1,2}\.$/.test(s) && i + 1 < sentences.length) {
      const next = sentences[i + 1]!.trim();
      if (next && !/^\d{1,2}\.\s/.test(next)) {
        out.push(`${s} ${next}`);
        i += 2;
        continue;
      }
    }
    out.push(s);
    i += 1;
  }
  return out;
}

/**
 * After splitting on {@link SENTENCE_BOUNDARY}, rejoin with spaces — except before ordered-list
 * lines (`1. …`) and `Phase N …` headers, where a newline must be preserved or MarkdownText
 * would glue items into one paragraph.
 */
function joinSentencesPreservingOrderedLists(sentences: string[]): string {
  if (sentences.length === 0) return '';
  let acc = sentences[0]!.trim();
  for (let i = 1; i < sentences.length; i++) {
    const s = sentences[i]!.trim();
    if (!s) continue;
    if (/^\d{1,2}\.\s/.test(s) || /^\d{1,2}\.$/.test(s) || /^Phase\s+\d+/iu.test(s)) {
      acc += `\n${s}`;
    } else {
      acc += ` ${s}`;
    }
  }
  return acc;
}

/**
 * Splits very long text blocks into paragraphs (few sentences each) so MarkdownText renders
 * multiple <p> instead of one unreadable wall.
 */
export function splitLongTextIntoParagraphs(text: string, sentencesPerParagraph = 3, minCharsToSplit = 380): string {
  const t = fixOrphanNumberedListLines(text.trim());
  if (!t) return '';

  const sentences = mergeOrphanListNumberWithFollowingSentence(
    t.split(SENTENCE_BOUNDARY).map((s) => s.trim()).filter(Boolean)
  );
  const mergedFull = joinSentencesPreservingOrderedLists(sentences);

  if (t.length < minCharsToSplit || sentences.length <= sentencesPerParagraph) {
    return mergedFull;
  }

  const paras: string[] = [];
  for (let i = 0; i < sentences.length; i += sentencesPerParagraph) {
    paras.push(joinSentencesPreservingOrderedLists(sentences.slice(i, i + sentencesPerParagraph)));
  }
  return paras.join('\n\n');
}

/**
 * Converts dense single-paragraph research summaries (typical for trend/market runs)
 * into lightweight Markdown with ## sections so MarkdownText can render readable structure.
 */
export function formatResearchSummaryToMarkdown(raw: string): string {
  const t = normalizeResearchSummaryRaw(raw);
  if (!t) return '';
  if (looksLikeStructuredMarkdown(t)) return splitDenseParagraphsInMarkdown(t);

  const splitRe =
    /(?=Danach folgt die Validierung|In(?: der Region Basel| Basel) sind|Sekundäre Suchpfade sind|Wettbewerb:\s|Kampagnenmuster, die bis|Chancen:\s|Risiken:\s|Ausblick:\s|Fazit:\s|Empfehlungen:\s|Executive\s+summary|Key\s+findings)/i;

  const parts = t
    .split(splitRe)
    .map((s) => s.trim())
    .filter(Boolean);

  if (parts.length <= 1) {
    if (/^#{1,3}\s/m.test(t)) {
      return splitDenseParagraphsInMarkdown(t);
    }
    return `## Zusammenfassung\n\n${splitLongTextIntoParagraphs(t)}`;
  }

  const blocks: string[] = [];
  for (const body of parts) {
    const title = inferSectionTitle(body);
    const content = stripLeadingLabel(body, title);
    blocks.push(`## ${title}\n\n${splitLongTextIntoParagraphs(content)}`);
  }

  return splitDenseParagraphsInMarkdown(blocks.join('\n\n'));
}

/**
 * Use for Companion / assistant bubbles: turns dense research or chat replies into readable Markdown.
 */
export function formatAssistantDisplayMarkdown(raw: string): string {
  const t = normalizeResearchSummaryRaw(raw);
  if (/^Fehler:/i.test(t)) return t;
  return formatResearchSummaryToMarkdown(t);
}

function looksLikeStructuredMarkdown(s: string): boolean {
  if (/\n###\s/.test(s)) return true;
  if (/\n##\s/.test(s)) return true;
  if (/^#{1,3}\s/m.test(s)) {
    const rest = s.replace(/^#{1,3}\s[^\n]+/m, '').trim();
    const paragraphs = rest.split(/\n\n+/).filter((x) => x.trim());
    return paragraphs.length >= 2 || rest.length < 420;
  }
  return false;
}

/** Split any remaining very long blocks (even under a ## section). */
function splitDenseParagraphsInMarkdown(md: string): string {
  return md
    .split(/\n\n+/)
    .map((block) => {
      const t = block.trim();
      if (!t) return '';
      if (/^#{1,3}\s/.test(t)) return t;
      return splitLongTextIntoParagraphs(t, 3, 280);
    })
    .filter(Boolean)
    .join('\n\n');
}

function inferSectionTitle(body: string): string {
  const s = body.trim();
  if (/^Prognose\b/i.test(s)) return 'Prognose';
  if (/^Danach folgt die Validierung/i.test(s)) return 'Validierung';
  if (/^In(?: der Region Basel| Basel) sind/i.test(s)) return 'Regionale Anforderungen';
  if (/^Sekundäre Suchpfade/i.test(s)) return 'Sekundäre Suchpfade';
  if (/^Wettbewerb:/i.test(s)) return 'Wettbewerb';
  if (/^Kampagnenmuster/i.test(s)) return 'Kampagnenmuster';
  if (/^Chancen:/i.test(s)) return 'Chancen';
  if (/^Risiken:/i.test(s)) return 'Risiken';
  return 'Zusammenfassung';
}

function stripLeadingLabel(body: string, title: string): string {
  const s = body.trim();
  if (title === 'Wettbewerb') return s.replace(/^Wettbewerb:\s*/i, '').trim();
  if (title === 'Chancen') return s.replace(/^Chancen:\s*/i, '').trim();
  if (title === 'Risiken') return s.replace(/^Risiken:\s*/i, '').trim();
  if (title === 'Kampagnenmuster') return s.replace(/^Kampagnenmuster[^:]*:\s*/i, '').trim();
  return s;
}
